import { useCallback, useEffect, useState } from 'react';

// T262 — a paid LLM result should survive a click on "Dashboard".
//
// The retro and cluster cards each held their result in useState, so navigating
// away and back threw away something the operator had paid Anthropic tokens
// for, and the only way to see it again was to pay again. That also runs
// against how the rest of the project treats generated content: Digest.js
// snapshots its output rather than re-deriving it on read.
//
// sessionStorage, not a server collection: this is one operator's transient
// reading state, it expires with the tab, and nothing else needs to read it.
// If he ever asks for a history of retros, that is a collection — not this.
//
// Keyed by (kind, variant) so switching Week/Month/Quarter, or the analytics
// range, shows that combination's own result rather than a stale one under a
// new label.

const PREFIX = 'hunter:ai:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const keyFor = (kind, variant) => `${PREFIX}${kind}:${variant ?? '_'}`;

// T290b — "one operator's transient reading state" was true when there was one
// operator. There are three accounts on this instance now, and sessionStorage
// survives a logout: it is scoped to the TAB, not to the session, so the next
// person to sign in here would open the retro and read the previous account's
// generated text. Cleared on the way out, like the module caches — see
// auth/AuthContext.jsx. Namespacing by user id would work too, but this content
// expires in 24h and is regenerated on demand, so there is nothing to preserve.
export function clearAiCache() {
  try {
    const doomed = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* private mode / storage disabled — nothing cached, nothing to clear */
  }
}

function read(kind, variant) {
  try {
    const raw = sessionStorage.getItem(keyFor(kind, variant));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.at || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    // Private mode, disabled storage, or a half-written entry. A cache that
    // throws is worse than no cache.
    return null;
  }
}

/**
 * Cached result for one paid generation.
 *
 * @returns {{data: any, generatedAt: number|null, save: (d:any)=>void, clear: ()=>void}}
 */
export function useAiCache(kind, variant) {
  const [entry, setEntry] = useState(() => read(kind, variant));

  useEffect(() => {
    setEntry(read(kind, variant));
  }, [kind, variant]);

  const save = useCallback(
    (data) => {
      const next = { data, at: Date.now() };
      setEntry(next);
      try {
        sessionStorage.setItem(keyFor(kind, variant), JSON.stringify(next));
      } catch {
        // Over quota or unavailable — the in-memory copy still works for this
        // visit, which is exactly the old behaviour.
      }
    },
    [kind, variant],
  );

  const clear = useCallback(() => {
    setEntry(null);
    try {
      sessionStorage.removeItem(keyFor(kind, variant));
    } catch {
      /* nothing to do */
    }
  }, [kind, variant]);

  return { data: entry?.data ?? null, generatedAt: entry?.at ?? null, save, clear };
}

/** "just now" / "3h ago" — coarse on purpose; the point is staleness, not precision. */
export function generatedAgo(at, now = Date.now()) {
  if (!at) return null;
  const mins = Math.floor((now - at) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
